import { useEffect, useState } from 'react';
import useStoreFilter from './useStoreFilter';
import { message, Popover } from 'antd';
import ShopeePerformanceTable from './ShopeePerformanceTable';
import { useRequest } from 'ahooks';
import { getStorePerfList } from './service';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import useModal from 'hooks/useModal';
import ShopeeTrendDrawer from './ShopeeTrendDrawer';
import { eventBus } from 'services/eventBus';
import dayjs from 'dayjs';
import { What } from 'views/Aide/Scenario/PolicyTable';
import { useCreateTrigger, useTriggerMapValue } from 'hooks/business/useTrigger';
import { CloseOutlined } from '@ant-design/icons';

function ShopeePerformance({ departments }: { departments: Dashboard.Department[] }) {
  const { t } = useTranslate();

  const { CSAT_DATA_TIPS_TEST } = useTriggerMapValue();

  const createTrigger = useCreateTrigger();

  const [tooltipVisible, setTooltipVisible] = useState(!CSAT_DATA_TIPS_TEST);

  const {
    selectedDepartments,
    departmentsSelect,
    storeType,
    storeTypeSelect,
    storeInfo,
    storeInfoSelect,
  } = useStoreFilter({
    platform: 'SHOPEE',
    departments,
  });

  const dateRender = (x: number) => dayjs().subtract(x, 'days').format('YYYY/MM/DD');

  const filter = (
    <header>
      <section>
        {departmentsSelect}
        {storeTypeSelect}
        {storeInfoSelect}
      </section>
      <div>
        {t('数据说明')}
        <Popover
          visible={tooltipVisible}
          onVisibleChange={(visible) => {
            setTooltipVisible(visible);
            if (!visible && !CSAT_DATA_TIPS_TEST) {
              createTrigger({ trigger: 'CSAT_DATA_TIPS_TEST' });
            }
          }}
          trigger={[CSAT_DATA_TIPS_TEST ? 'hover' : 'click']}
          placement="topRight"
          content={
            <div className="max-w-[562px] relative">
              {!CSAT_DATA_TIPS_TEST && (
                <CloseOutlined
                  className="absolute top-[-8px] right-[-8px]"
                  onClick={() => {
                    createTrigger({ trigger: 'CSAT_DATA_TIPS_TEST' });
                    setTooltipVisible(false);
                  }}
                />
              )}
              <div>
                ·{' '}
                {t('店铺评价与聊天数据统计区间：x-y', {
                  x: dateRender(31),
                  y: dateRender(2),
                })}
              </div>
              <div>
                ·{' '}
                {t('订单数据统计区间：x-y', {
                  x: dateRender(8),
                  y: dateRender(2),
                })}
              </div>
              <div>
                ·{' '}
                {t(
                  '聊天满意度目前接口仅开放了印尼、马来西亚、新加坡站点；首次/平均响应时长目前接口仅开放了马来西亚、菲律宾、新加坡、泰国站点。',
                )}
              </div>
            </div>
          }
        >
          <What />
        </Popover>
      </div>
    </header>
  );

  const checkDetail = ({
    storeId,
    region,
    storeName,
    storeAlias,
  }: Dashboard.PlatformData.StorePerfData) => {
    eventBus.emit('CHECK_STORE_PLATFORM_CSAT_DETAIL', {
      id: storeId as any,
      platform: 'SHOPEE',
      region,
      storeName,
      storeNameAlias: storeAlias,
      storeType: 'LOCAL',
    });
  };

  const { visible, show, hide, meta } = useModal<Dashboard.PlatformData.StorePerfData>();

  const drawer = meta && (
    <ShopeeTrendDrawer
      key={meta.storeId}
      visible={visible}
      onClose={() => hide()}
      storePerfData={meta}
      onCheckDetail={checkDetail}
    />
  );

  const [sortKey, setSortKey] = useState<string>();
  const [sortType, setSortType] = useState<1 | 2>();

  const { loading, data, run } = useRequest(
    (
      page: {
        pageNum: number;
        pageSize: number;
      } = {
        pageNum: 1,
        pageSize: 10,
      },
    ) => {
      return getStorePerfList({
        ...page,
        sortKey,
        sortType,
        platform: 'SHOPEE',
        departmentIds: selectedDepartments.map(({ id }) => id),
        storeType: storeType === 'CROSS' ? '跨境' : storeType === 'LOCAL' ? '本土' : undefined,
        storeIds: storeInfo.list?.map(({ id }) => Number(id)),
      });
    },
    {
      manual: true,
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  useEffect(() => {
    run();
  }, [run, sortKey, sortType, selectedDepartments, storeType, storeInfo]);

  const table = (
    <ShopeePerformanceTable
      loading={loading}
      dataSource={data?.list}
      pagination={{
        current: data?.pageNum || 1,
        pageSize: data?.pageSize || 10,
        total: data?.total,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (x) => t('feedback.共x项', { x }),
      }}
      onChange={(page: any, _, { order, field }: any) => {
        if (page.current === (data?.pageNum || 1) && page.pageSize === (data?.pageSize || 10)) {
          setSortKey(order && field);
          setSortType(order === 'ascend' ? 1 : order === 'descend' ? 2 : undefined);
        } else {
          run({
            pageNum: page.current,
            pageSize: page.pageSize,
          });
        }
      }}
      onCheckDetail={checkDetail}
      onCheckTrend={show}
    />
  );

  return (
    <Container>
      {filter}
      <section>{table}</section>
      {drawer}
    </Container>
  );
}

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  > header {
    flex: 0 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 16px;
    margin-bottom: 16px;
    margin-top: 1px;
    background: var(--fett-bg-panel);
    > section {
      flex: 1 0 0;
      display: flex;
      align-items: center;
      > * {
        margin-right: 16px;
      }
    }
  }
  > div {
    background-color: var(--fett-surface-warning-border);
    button {
      height: 17px;
      margin-top: -4px;
    }
  }
  > section {
    flex: 1 0 0;
    padding: 16px;
    background: var(--fett-bg-panel);
  }
`;

export default ShopeePerformance;
